import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@constants/api.routes";
import {
	fetchEmploymentStatuses,
	fetchEmploymentActivity,
	fetchStudentOutcomeCurrent,
	fetchHigherStudy,
	fetchEmployers,
	fetchOutcomeJobTypes,
	fetchCountries,
	fetchDistricts,
	fetchCitiesByDistrict,
	type EmploymentActivity,
	type HigherStudy,
	type StudentOutcomeCurrent,
	type Employer,
	type OutcomeJobType,
	type LocationOption,
	type PaginatedListResponse,
} from "./statusService";
import type { StatusOptionConfig } from "../types/status.types";
import type { AxiosError } from "axios";
import React from "react";
import {
	toEmployerSelectOptions,
	toLocationSelectOptions,
	toOutcomeJobTypeSelectOptions,
} from "../utils/statusUtils";

type PaginatedList<T> = { data?: T[]; pagination?: unknown };

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

const normalizeMaybeListToArray = <T>(raw: unknown): T[] => {
	// Handles: T[] | { data: T[] } | { data: { data: T[] } } | null
	if (!raw) return [];
	if (Array.isArray(raw)) return raw as T[];
	if (!isRecord(raw)) return [];

	const direct = (raw as PaginatedList<T>).data;
	if (Array.isArray(direct)) return direct;

	if (isRecord((raw as { data?: unknown }).data)) {
		return normalizeMaybeListToArray<T>((raw as { data?: unknown }).data);
	}

	return [];
};

const normalizeMaybeListResponse = <T>(raw: unknown): T | null => {
	// Handles: T | T[] | { data: T[] } | { data: { data: T[] } } | null
	if (!raw) return null;

	if (Array.isArray(raw)) {
		return (raw[0] as T | undefined) ?? null;
	}

	if (!isRecord(raw)) return null;

	// Case: { data: [...] }
	if (Array.isArray((raw as PaginatedList<T>).data)) {
		return (raw as PaginatedList<T>).data?.[0] ?? null;
	}

	// Case: { data: { data: [...] } }
	if (isRecord(raw.data)) {
		return normalizeMaybeListResponse<T>((raw as { data?: unknown }).data);
	}

	// Case: already an object record for T
	return raw as unknown as T;
};

// Determine if a status requires a modal based on API code
const requiresModal = (code: string): boolean => {
	return code === "EMPLOYED" || code === "SELF_EMPLOYED" || code.startsWith("HIGHER");
};

// Transform API response to StatusOptionConfig using API data directly
const transformStatusToConfig = (status: {
	id: number;
	uuid: string;
	code: string;
	name: string;
}): StatusOptionConfig & { apiCode: string; statusId: string } => {
	// Map API code to modal type for statusType prop
	const getModalType = (code: string): "Employed" | "Self Employed" | "Higher Studies" | null => {
		if (code === "EMPLOYED") return "Employed";
		if (code === "SELF_EMPLOYED") return "Self Employed";
		if (code.startsWith("HIGHER")) return "Higher Studies";
		return null;
	};

	return {
		value: status.name,
		label: status.name,
		requiresModal: requiresModal(status.code),
		apiCode: status.code,
		modalType: getModalType(status.code),
		statusId: status.uuid, // Use UUID for API call
	};
};

export const useEmploymentStatuses = () => {
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: [QUERY_KEYS.STATUS.EMPLOYMENT_STATUSES],
		queryFn: async () => {
			const response = await fetchEmploymentStatuses();
			return response.data;
		},
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

	// Transform API data to StatusOptionConfig format
	const statusOptions: StatusOptionConfig[] = data ? data.map(transformStatusToConfig) : [];

	return {
		employmentStatuses: statusOptions,
		loading: isLoading,
		error,
		refetch,
	};
};

export type EmploymentActivityType = "EMPLOYMENT" | "SELF_EMPLOYMENT";

export const useEmploymentActivity = (activityType: EmploymentActivityType | null, enabled: boolean = true) => {
	const { data, isLoading, error, refetch } = useQuery<EmploymentActivity | null>({
		queryKey: [QUERY_KEYS.STATUS.EMPLOYMENT_ACTIVITY, activityType ?? "ALL"],
		queryFn: async () => {
			try {
				// Pass activityType as query param so backend filters
				const response = await fetchEmploymentActivity(activityType ?? undefined);
				const raw: unknown = response.data;
				const listFromArray = normalizeMaybeListToArray<EmploymentActivity>(raw);
				const list =
					listFromArray.length > 0
						? listFromArray
						: (() => {
								const single = normalizeMaybeListResponse<EmploymentActivity>(raw);
								return single ? [single] : [];
						  })();
				// Fallback client-side filter (backend may return multiple records)
				const filtered = activityType
					? list.filter(item => item.activityType === activityType || item.type === activityType)
					: list;
				return filtered[0] ?? null;
			} catch (err) {
				// If no data exists (404), return null instead of throwing
				const axiosErr = err as AxiosError | undefined;
				const status = axiosErr?.response?.status;
				if (status === 404) return null;
				throw err;
			}
		},
		enabled,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

	return {
		employmentActivity: data,
		loading: isLoading,
		error,
		refetch,
	};
};

// Hook to fetch ALL employment activities (not just the first one)
export const useEmploymentActivities = (activityType: EmploymentActivityType | null, enabled: boolean = true) => {
	const { data, isLoading, error, refetch } = useQuery<EmploymentActivity[]>({
		queryKey: [QUERY_KEYS.STATUS.EMPLOYMENT_ACTIVITY, "ALL_LIST", activityType ?? "ALL"],
		queryFn: async () => {
			try {
				const response = await fetchEmploymentActivity(activityType ?? undefined);
				const raw: unknown = response.data;
				const listFromArray = normalizeMaybeListToArray<EmploymentActivity>(raw);
				const list =
					listFromArray.length > 0
						? listFromArray
						: (() => {
								const single = normalizeMaybeListResponse<EmploymentActivity>(raw);
								return single ? [single] : [];
						  })();
				// Fallback client-side filter (backend may return multiple records)
				const filtered = activityType
					? list.filter(item => item.activityType === activityType || item.type === activityType)
					: list;
				return filtered;
			} catch (err) {
				const axiosErr = err as AxiosError | undefined;
				const status = axiosErr?.response?.status;
				if (status === 404) return [];
				throw err;
			}
		},
		enabled,
		staleTime: 5 * 60 * 1000,
	});

	return {
		employmentActivities: data ?? [],
		loading: isLoading,
		error,
		refetch,
	};
};

export const useStudentOutcomeCurrent = (enabled: boolean = true) => {
	const { data, isLoading, error, refetch } = useQuery<StudentOutcomeCurrent | null>({
		queryKey: [QUERY_KEYS.STATUS.OUTCOME_CURRENT],
		queryFn: async () => {
			const response = await fetchStudentOutcomeCurrent();
			return response?.data ?? null;
		},
		enabled,
		staleTime: 60 * 1000, // 1 minute
	});

	return {
		outcomeCurrent: data,
		loading: isLoading,
		error,
		refetch,
	};
};

export const useHigherStudy = (enabled: boolean = true) => {
	const { data, isLoading, error, refetch } = useQuery<HigherStudy | null>({
		queryKey: [QUERY_KEYS.STATUS.HIGHER_STUDY],
		queryFn: async () => {
			try {
				const response = await fetchHigherStudy();
				return normalizeMaybeListResponse<HigherStudy>(response?.data);
			} catch (err) {
				// If no data exists (404), return null instead of throwing
				const axiosErr = err as AxiosError | undefined;
				const status = axiosErr?.response?.status;
				if (status === 404) return null;
				throw err;
			}
		},
		enabled,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

	return {
		higherStudy: data,
		loading: isLoading,
		error,
		refetch,
	};
};

// Hook to fetch ALL higher studies (not just the first one)
export const useHigherStudies = (enabled: boolean = true) => {
	const { data, isLoading, error, refetch } = useQuery<HigherStudy[]>({
		queryKey: [QUERY_KEYS.STATUS.HIGHER_STUDY, "ALL_LIST"],
		queryFn: async () => {
			try {
				const response = await fetchHigherStudy();
				const raw: unknown = response?.data;
				const listFromArray = normalizeMaybeListToArray<HigherStudy>(raw);
				if (listFromArray.length > 0) return listFromArray;
				const single = normalizeMaybeListResponse<HigherStudy>(raw);
				return single ? [single] : [];
			} catch (err) {
				const axiosErr = err as AxiosError | undefined;
				const status = axiosErr?.response?.status;
				if (status === 404) return [];
				throw err;
			}
		},
		enabled,
		staleTime: 5 * 60 * 1000,
	});

	return {
		higherStudies: data ?? [],
		loading: isLoading,
		error,
		refetch,
	};
};

const emptyPagination = {
	currentPage: 1,
	perPage: 10,
	totalItems: 0,
	totalPages: 0,
	hasNextPage: false,
	hasPreviousPage: false,
};

const parsePaginatedPage = <T>(raw: unknown): PaginatedListResponse<T> => {
	if (!raw || typeof raw !== "object") {
		return { data: [], pagination: emptyPagination };
	}

	const nested = (raw as { data?: unknown }).data;
	if (typeof nested === "object" && nested !== null && Array.isArray((nested as PaginatedListResponse<T>).data)) {
		const pageData = nested as PaginatedListResponse<T>;
		return {
			data: pageData.data ?? [],
			pagination: pageData.pagination ?? emptyPagination,
		};
	}

	const rootData = (raw as PaginatedListResponse<T>).data;
	if (Array.isArray(rootData)) {
		const pageData = raw as PaginatedListResponse<T>;
		return {
			data: pageData.data ?? [],
			pagination: pageData.pagination ?? emptyPagination,
		};
	}

	return { data: [], pagination: emptyPagination };
};

export const useEmployers = (searchTerm?: string, enabled = true) => {
	const normalizedSearchTerm = searchTerm?.trim() || undefined;

	const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<
		PaginatedListResponse<Employer>
	>({
		queryKey: [QUERY_KEYS.STATUS.EMPLOYERS, normalizedSearchTerm],
		queryFn: async ({ pageParam = 1 }) => {
			const response = await fetchEmployers({
				page: pageParam as number,
				limit: 10,
				...(normalizedSearchTerm ? { search: normalizedSearchTerm } : {}),
			});
			return parsePaginatedPage<Employer>(response);
		},
		initialPageParam: 1,
		getNextPageParam: lastPage => {
			if (lastPage?.pagination?.hasNextPage) {
				return (lastPage.pagination?.currentPage ?? 1) + 1;
			}
			return undefined;
		},
		enabled,
	});

	const employers = data?.pages?.flatMap(page => page.data ?? []) ?? [];
	const employerOptions = React.useMemo(() => toEmployerSelectOptions(employers), [employers]);

	return {
		employers,
		employerOptions,
		loadingEmployers: isLoading,
		fetchNextEmployers: fetchNextPage,
		hasNextEmployers: hasNextPage ?? false,
		isFetchingNextEmployers: isFetchingNextPage ?? false,
	};
};

export const useOutcomeJobTypes = (outcomeStatusUuid?: string, searchTerm?: string, enabled = true) => {
	const normalizedSearchTerm = searchTerm?.trim() || undefined;

	const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<
		PaginatedListResponse<OutcomeJobType>
	>({
		queryKey: [QUERY_KEYS.STATUS.OUTCOME_JOB_TYPES, outcomeStatusUuid, normalizedSearchTerm],
		queryFn: async ({ pageParam = 1 }) => {
			const response = await fetchOutcomeJobTypes({
				page: pageParam as number,
				limit: 10,
				outcomeStatusUuid: outcomeStatusUuid!,
				...(normalizedSearchTerm ? { search: normalizedSearchTerm } : {}),
			});
			return parsePaginatedPage<OutcomeJobType>(response);
		},
		initialPageParam: 1,
		getNextPageParam: lastPage => {
			if (lastPage?.pagination?.hasNextPage) {
				return (lastPage.pagination?.currentPage ?? 1) + 1;
			}
			return undefined;
		},
		enabled: enabled && !!outcomeStatusUuid,
	});

	const outcomeJobTypes = data?.pages?.flatMap(page => page.data ?? []) ?? [];
	const jobTypeOptions = React.useMemo(() => toOutcomeJobTypeSelectOptions(outcomeJobTypes), [outcomeJobTypes]);

	return {
		outcomeJobTypes,
		jobTypeOptions,
		loadingJobTypes: isLoading,
		fetchNextJobTypes: fetchNextPage,
		hasNextJobTypes: hasNextPage ?? false,
		isFetchingNextJobTypes: isFetchingNextPage ?? false,
	};
};

const usePaginatedLocationQuery = (
	queryKey: string,
	fetchFn: (params: { page: number; limit: number; search?: string }) => Promise<unknown>,
	searchTerm?: string,
	enabled = true,
) => {
	const normalizedSearchTerm = searchTerm?.trim() || undefined;

	const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<
		PaginatedListResponse<LocationOption>
	>({
		queryKey: [queryKey, normalizedSearchTerm],
		queryFn: async ({ pageParam = 1 }) => {
			const response = await fetchFn({
				page: pageParam as number,
				limit: 10,
				...(normalizedSearchTerm ? { search: normalizedSearchTerm } : {}),
			});
			return parsePaginatedPage<LocationOption>(response);
		},
		initialPageParam: 1,
		getNextPageParam: lastPage => {
			if (lastPage?.pagination?.hasNextPage) {
				return (lastPage.pagination?.currentPage ?? 1) + 1;
			}
			return undefined;
		},
		enabled,
	});

	const locations = data?.pages?.flatMap(page => page.data ?? []) ?? [];
	const options = React.useMemo(() => toLocationSelectOptions(locations), [locations]);

	return {
		locations,
		options,
		loading: isLoading,
		fetchNextPage,
		hasNextPage: hasNextPage ?? false,
		isFetchingNextPage: isFetchingNextPage ?? false,
	};
};

export const useCountries = (searchTerm?: string, enabled = true) => {
	const result = usePaginatedLocationQuery(
		QUERY_KEYS.STATUS.COUNTRIES,
		params => fetchCountries(params),
		searchTerm,
		enabled,
	);

	return {
		countries: result.locations,
		countryOptions: result.options,
		loadingCountries: result.loading,
		fetchNextCountries: result.fetchNextPage,
		hasNextCountries: result.hasNextPage,
		isFetchingNextCountries: result.isFetchingNextPage,
	};
};

export const useDistricts = (searchTerm?: string, enabled = true) => {
	const result = usePaginatedLocationQuery(
		QUERY_KEYS.STATUS.DISTRICTS,
		params => fetchDistricts(params),
		searchTerm,
		enabled,
	);

	return {
		districts: result.locations,
		districtOptions: result.options,
		loadingDistricts: result.loading,
		fetchNextDistricts: result.fetchNextPage,
		hasNextDistricts: result.hasNextPage,
		isFetchingNextDistricts: result.isFetchingNextPage,
	};
};

export const useCities = (districtId?: string, searchTerm?: string, enabled = true) => {
	const normalizedSearchTerm = searchTerm?.trim() || undefined;

	const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<
		PaginatedListResponse<LocationOption>
	>({
		queryKey: [QUERY_KEYS.STATUS.CITIES, districtId, normalizedSearchTerm],
		queryFn: async ({ pageParam = 1 }) => {
			const response = await fetchCitiesByDistrict(districtId!, {
				page: pageParam as number,
				limit: 10,
				...(normalizedSearchTerm ? { search: normalizedSearchTerm } : {}),
			});
			return parsePaginatedPage<LocationOption>(response);
		},
		initialPageParam: 1,
		getNextPageParam: lastPage => {
			if (lastPage?.pagination?.hasNextPage) {
				return (lastPage.pagination?.currentPage ?? 1) + 1;
			}
			return undefined;
		},
		enabled: enabled && !!districtId,
	});

	const cities = data?.pages?.flatMap(page => page.data ?? []) ?? [];
	const cityOptions = React.useMemo(() => toLocationSelectOptions(cities), [cities]);

	return {
		cities,
		cityOptions,
		loadingCities: isLoading,
		fetchNextCities: fetchNextPage,
		hasNextCities: hasNextPage ?? false,
		isFetchingNextCities: isFetchingNextPage ?? false,
	};
};
